import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud } from "lucide-react";
import { api, errMessage } from "../../lib/api";
import { mediaSrc, TIMEZONE_LABEL } from "../../lib/media";

/** Extract the first decodable frame of a video in the browser (no server transcoding). */
function extractPoster(file) {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.preload = "metadata";
    video.muted = true;
    video.playsInline = true;
    video.src = URL.createObjectURL(file);
    const fail = () => reject(new Error("poster"));
    video.onerror = fail;
    video.onloadeddata = () => {
      video.currentTime = Math.min(0.5, (video.duration || 1) / 2);
    };
    video.onseeked = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        if (!canvas.width || !canvas.height) return fail();
        canvas.getContext("2d").drawImage(video, 0, 0);
        canvas.toBlob(
          (blob) => (blob ? resolve({ blob, url: URL.createObjectURL(blob) }) : fail()),
          "image/jpeg",
          0.8
        );
      } catch {
        fail();
      }
    };
    setTimeout(fail, 8000);
  });
}

export default function AdminMediaUpload({ onUploaded }) {
  const [info, setInfo] = useState(null);
  const [file, setFile] = useState(null);
  const [caption, setCaption] = useState("");
  const [eventId, setEventId] = useState("");
  const [partnerId, setPartnerId] = useState("");
  const [creatorId, setCreatorId] = useState("");
  const [capturedAt, setCapturedAt] = useState("");
  const [progress, setProgress] = useState(null);
  const [preview, setPreview] = useState(null);
  const [posterBlob, setPosterBlob] = useState(null);
  const [options, setOptions] = useState({ events: [], partners: [], profiles: [] });
  const inputRef = useRef(null);

  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setInfo(data.media_storage)).catch(() => {});
    Promise.all([api.get("/admin/events"), api.get("/admin/partners"), api.get("/admin/profiles")])
      .then(([e, p, pr]) => setOptions({ events: e.data, partners: p.data, profiles: pr.data }))
      .catch(() => {});
  }, []);

  const pick = (f) => {
    setFile(f || null);
    setPosterBlob(null);
    if (!f) return setPreview(null);
    if (f.type.startsWith("image/")) return setPreview(URL.createObjectURL(f));
    setPreview(null);
    extractPoster(f)
      .then(({ blob, url }) => {
        setPosterBlob(blob);
        setPreview(url);
      })
      .catch(() => setPosterBlob(null));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    if (posterBlob) form.append("poster", posterBlob, "poster.jpg");
    if (caption) form.append("caption", caption);
    if (eventId) form.append("event_id", eventId);
    if (partnerId) form.append("partner_id", partnerId);
    if (creatorId) form.append("creator_id", creatorId);
    if (capturedAt) form.append("captured_at", capturedAt);
    setProgress(0);
    try {
      const { data } = await api.post("/admin/media/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) =>
          setProgress(evt.total ? Math.round((evt.loaded * 100) / evt.total) : null),
      });
      toast.success("Média téléversé — statut brouillon (non publié)");
      setFile(null);
      setPreview(null);
      setPosterBlob(null);
      setCaption("");
      setCapturedAt("");
      if (inputRef.current) inputRef.current.value = "";
      onUploaded?.(data);
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setProgress(null);
    }
  };

  return (
    <form onSubmit={submit} className="vb-card p-6 lg:p-8 mb-10" data-testid="admin-media-upload-panel">
      <div className="vb-label">Téléversement</div>
      <h2 className="vb-display text-2xl mt-3">Ajouter un média</h2>
      <p className="mt-3 text-sm text-neutral-400 max-w-3xl">
        JPEG, PNG, WebP (max {info?.max_image_mb ?? 10} Mo) · MP4, WebM (max {info?.max_video_mb ?? 150} Mo).
        Un média téléversé reste en <span className="text-white">brouillon</span> et n'est jamais publié
        automatiquement. Le consentement demeure un registre distinct.
        {info && (
          <span className="block mt-2 text-neutral-500" data-testid="admin-media-provider-info">
            Fournisseur : {info.provider} · téléversement {info.upload_enabled ? "actif" : "désactivé (mode démo)"}
          </span>
        )}
      </p>

      <div className="mt-6 grid md:grid-cols-2 gap-6">
        <div className="md:col-span-2">
          <label className="vb-label">Fichier</label>
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,video/mp4,video/webm"
            onChange={(e) => pick(e.target.files?.[0])}
            data-testid="admin-media-file-input"
            className="vb-input file:mr-4 file:py-2 file:px-4 file:border-0 file:bg-white/10 file:text-white file:text-xs file:rounded-full"
          />
        </div>
        <div>
          <label className="vb-label">Légende</label>
          <input className="vb-input" value={caption} onChange={(e) => setCaption(e.target.value)} data-testid="admin-media-caption" />
        </div>
        <div>
          <label className="vb-label">Capturé le</label>
          <input
            className="vb-input [color-scheme:dark]"
            type="datetime-local"
            value={capturedAt}
            onChange={(e) => setCapturedAt(e.target.value)}
            data-testid="admin-media-captured-at"
          />
          <div className="mt-2 text-[11px] text-neutral-400">{TIMEZONE_LABEL}</div>
        </div>
        <div>
          <label className="vb-label">Événement (optionnel)</label>
          <select className="vb-input" value={eventId} onChange={(e) => setEventId(e.target.value)} data-testid="admin-media-event">
            <option value="" className="bg-black">—</option>
            {options.events.map((o) => (
              <option key={o.id} value={o.id} className="bg-black">{o.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="vb-label">Partenaire (optionnel)</label>
          <select className="vb-input" value={partnerId} onChange={(e) => setPartnerId(e.target.value)} data-testid="admin-media-partner">
            <option value="" className="bg-black">—</option>
            {options.partners.map((o) => (
              <option key={o.id} value={o.id} className="bg-black">{o.business_name}</option>
            ))}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="vb-label">Créateur / profil (optionnel)</label>
          <select className="vb-input" value={creatorId} onChange={(e) => setCreatorId(e.target.value)} data-testid="admin-media-creator">
            <option value="" className="bg-black">—</option>
            {options.profiles.map((o) => (
              <option key={o.id} value={o.id} className="bg-black">{o.display_name}</option>
            ))}
          </select>
        </div>
      </div>

      {preview && (
        <img
          src={preview}
          alt="Aperçu"
          data-testid="admin-media-preview"
          onError={() => setPreview(null)}
          className="mt-6 h-40 w-auto object-cover border border-white/10"
        />
      )}
      {file && file.type.startsWith("video/") && (
        <div className="mt-3 text-xs text-neutral-500" data-testid="admin-media-poster-state">
          {posterBlob
            ? "Affiche vidéo extraite automatiquement (première image)."
            : "Aucune affiche extraite — une tuile vidéo stylisée sera utilisée."}
        </div>
      )}

      {progress !== null && (
        <div className="mt-6" data-testid="admin-media-progress">
          <div className="h-1 bg-white/10">
            <div className="h-1 bg-[#FF2E63] transition-all" style={{ width: `${progress}%` }} />
          </div>
          <div className="mt-2 text-xs text-neutral-400">Téléversement… {progress}%</div>
        </div>
      )}

      <button
        type="submit"
        disabled={!file || progress !== null || info?.upload_enabled === false}
        className="vb-btn vb-btn-primary mt-8 disabled:opacity-40 disabled:cursor-not-allowed"
        data-testid="admin-media-upload-submit"
      >
        <UploadCloud size={16} /> Téléverser en brouillon
      </button>
    </form>
  );
}

export const MediaThumb = ({ url }) => (url ? <img src={mediaSrc(url)} alt="" className="h-10 w-10 object-cover" /> : null);
